import * as React from 'react';

import { BaseParams } from '@common/typescript/objects/BaseParams';
import FiltersComponent, { FiltersComponentProps } from '@common/react/components/UI/FiltersComponent/FiltersComponent';

type FiltersHandlerType<T extends BaseParams = BaseParams> = {
	[key in keyof T]?: (value: T[key]) => boolean;
}

export interface FilterPropsHandlerProps<T extends BaseParams = BaseParams> extends Omit<FiltersComponentProps, 'filters' | 'filtersClean'> {
	/**
	 * filters
	 */
	additionalParams: T;
	/**
	 * react node with contains filters node
	 */
	tooltipValues: JSX.Element;
	/**
	 * filters clean callback
	 * @param filterNames - object with properties of non-empty filters.
	 * if additionalParams = { some1: 3, some2: undefined }, filterNames will be { some1: undefined }
	 */
	filtersClean?: (filterNames: { [kei in keyof T]: undefined }) => void;
	/**
	 * an object with properties that define which filters are not empty.
	 *
	 * example: { someProperty: (value) => typeof value === 'number' && value > 0 }
	 */
	filtersHandler?: FiltersHandlerType<T>;
	/**
	 * custom component. if you need to place filters not in FiltersComponent (popover).
	 */
	customNode?: React.FC<FiltersComponentProps>;
	/**
	 * function to get filters node list from tooltipValues
	 * @param tooltipValues
	 */
	getChildrenList?: (tooltipValues: JSX.Element) => any;
	/**
	 * filters node visibility
	 */
	open?: boolean;
}

const getFilterHandler = (value: any): () => boolean => {
	if (value === undefined || value === null || value === '') {
		return () => false;
	}
	if (typeof value === 'boolean') {
		return () => true;
	}
	if (typeof value === 'number') {
		return (): boolean => value > -1;
	}
	if (typeof value === 'string') {
		if (!isNaN(+value)) {
			return (): boolean => +value > -1;
		}
		return () => true;
	}
	if (Array.isArray(value)) {
		return (): boolean => value.length > 0;
	}
	return () => false;
};

export const getFilters = (params: BaseParams, node: React.ReactElement, options: { filtersHandler: FiltersHandlerType }) => {
	const { filtersHandler = {} } = options;
	const filters: Array<() => boolean> = [];
	return [...React.Children.map(
		node,
		(child) => {
			const props = child?.props;
			if (typeof props === 'undefined') {
				return () => false;
			}

			if (Array.isArray(props['data-param'])) {
				props['data-param'].slice(1).forEach((value) => {
					filters.push(filtersHandler[value] ? () => !!filtersHandler[value]?.(params[value])
						: getFilterHandler(params[value]));
				});
				const key = props['data-param']?.[0]; const
					value = params[key];
				return filtersHandler[key] ? () => !!filtersHandler[key]?.(value) : getFilterHandler(value);
			}
			const key = props['data-param'] ? props['data-param'] : props.param;
			const value = params[key];

			return filtersHandler[key] ? () => !!filtersHandler[key]?.(value) : getFilterHandler(value);
		},
	), ...filters];
};

export const getClean = (node: React.ReactElement, { filtersClean }) => {
	return () => {
		if (filtersClean) {
			const clearFilters: BaseParams = {};

			React.Children.map(node, (filter) => {
				const props = filter?.props;
				if (props && (props['data-param'] || props.param)) {
					if (Array.isArray(props['data-param'] || props.param)) {
						(props['data-param'] || props.param).forEach((param: string) => clearFilters[param] = undefined);
					} else {
						clearFilters[props['data-param'] || props.param] = undefined;
					}
				}
			});

			filtersClean(clearFilters);
		}
	};
};

export const FilterNode: React.FC<{ filters: BaseParams, children: any, filtersHandler: FiltersHandlerType }> = ({
	filters, children, filtersHandler = {},
}) => {
	const param = typeof children === 'object' ? children?.props?.['data-param'] || children?.props?.param : '';
	const notDefault = Array.isArray(param)
		? param.some((item) => (filtersHandler[item] || getFilterHandler(filters?.[item]))(filters?.[item]))
		: (filtersHandler[param] || getFilterHandler(filters?.[param]))(filters?.[param]);

	return <>
		{param && notDefault && <span className="selected-filter" />}
		{children}
	</>;
};

const defaultGetChildrenList = (tooltipValues: React.ReactElement) => tooltipValues?.props?.children;

/**
 * FilterPropsHandler component.
 *
 * component handle filters node and solve filters count.
 * It is recommended to use FilterPropsHandler instead of FiltersComponent.
 * filters must be with props 'param' or 'data-param' so that the number of filters can be calculated from the structure of react elements
 *
 * @example:
	<FiltersComponent
		additionalParams={{}}
		tooltipValues={<div>
			<AutocompleteFilter type="someType"
				handleChangeAndRefresh={() => {}}
				param="someParam"
			/>
			<div className="filter-element" data-param="someParam2">
				<FilterBySomeParam2 />
			</div>
		</div>}
	/>
 *
 * @typeParam T - T Any {WithKey}
 * @param props - FilterPropsHandlerProps<T>
 * @returns React.ReactElement
 */
const FilterPropsHandler = <T extends BaseParams = BaseParams>({
	tooltipValues,
	additionalParams,
	apply,
	filtersClean,
	disabled = false,
	filtersHandler = {},
	getChildrenList = defaultGetChildrenList,
	customNode: CustomNode = FiltersComponent,
	placement,
	...rest
}: FilterPropsHandlerProps<T>) => {
	const filters: Array<() => boolean> = getFilters(additionalParams, getChildrenList(tooltipValues), { filtersHandler });

	const clean = React.useMemo(() => {
		return getClean(getChildrenList(tooltipValues), { filtersClean });
	}, [tooltipValues]);

	return (
		<CustomNode
			apply={apply}
			tooltipValues={defaultGetChildrenList === getChildrenList
				? React.cloneElement(tooltipValues, {
					...tooltipValues?.props,
					children: React.Children.map(
						tooltipValues?.props?.children,
						(child) => <FilterNode
							{...(typeof child === 'object' ? child?.props : {})}
							filtersHandler={filtersHandler}
							filters={additionalParams}
						>
							{child}
						</FilterNode>,
					),
				})
				: tooltipValues}
			filters={filters}
			filtersClean={clean}
			disabled={disabled}
			placement={placement as any}
			{...rest}
		/>
	);
};

export default FilterPropsHandler;
